<template>
  <div v-if="mode" class="pwa-banner" role="region" aria-label="Приложение QClient">
    <span class="pwa-ico"><AppIcon name="bell" :size="18" /></span>
    <div class="pwa-text">
      <template v-if="mode === 'install'">
        <strong>Установите QClient на телефон</strong>
        <span v-if="ios">Нажмите «Поделиться» в Safari и выберите «На экран Домой»: так откроется приложение, а в нём можно включить уведомления.</span>
        <span v-else>Приложение открывается с экрана, а новые записи приходят уведомлением.</span>
      </template>
      <template v-else>
        <strong>Включить уведомления о записях?</strong>
        <span>Новая запись, отмена или перенос придут на это устройство.</span>
      </template>
    </div>
    <button v-if="mode === 'install' && canInstall" type="button" class="pwa-btn" @click="install">Установить</button>
    <button v-else-if="mode === 'push'" type="button" class="pwa-btn" :disabled="busy" @click="enable">Включить</button>
    <button type="button" class="pwa-x" aria-label="Скрыть" @click="hide">Позже</button>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import AppIcon from './AppIcon.vue'
import { useToastStore } from '../stores/toast'
import {
  isStandalone, isIOS, canPromptInstall, promptInstall, onPwaChange, installDismissed, dismissInstall,
  pushBlocker, currentSubscription, enablePush
} from '../utils/pwa'

const toast = useToastStore()
const tick = ref(0)
const hidden = ref(false)
const busy = ref(false)
const hasSub = ref(true) // пока не проверили, не показываем
const ios = isIOS()
const PUSH_ASKED = 'pwa_push_asked_at'
const get = (k) => { try { return localStorage.getItem(k) } catch { return null } }
const set = (k, v) => { try { localStorage.setItem(k, v) } catch { /* ok */ } }

const canInstall = computed(() => (tick.value, canPromptInstall()))
const mobile = window.matchMedia && window.matchMedia('(max-width: 900px)').matches

const mode = computed(() => {
  tick.value
  if (hidden.value) return null
  if (isStandalone()) {
    const recentlyAsked = Date.now() - Number(get(PUSH_ASKED) || 0) < 14 * 864e5
    return !pushBlocker() && Notification.permission === 'default' && !hasSub.value && !recentlyAsked ? 'push' : null
  }
  if (installDismissed()) return null
  // iOS показываем подсказку всегда (кнопки установки там нет), остальным только если браузер предлагает установку
  return (ios && mobile) || canInstall.value ? 'install' : null
})

let off = null
onMounted(async () => {
  off = onPwaChange(() => { tick.value++ })
  try { hasSub.value = !!(await currentSubscription()) } catch { hasSub.value = false }
})
onUnmounted(() => off && off())

async function install() { await promptInstall(); tick.value++ }
async function enable() {
  busy.value = true
  try {
    await enablePush()
    hasSub.value = true
    toast.success('Уведомления включены')
  } catch (e) {
    set(PUSH_ASKED, String(Date.now()))
    toast.error(e.message || 'Не удалось включить уведомления')
  } finally { busy.value = false }
}
function hide() {
  if (mode.value === 'install') dismissInstall(); else set(PUSH_ASKED, String(Date.now()))
  hidden.value = true
}
</script>

<style scoped>
.pwa-banner {
  display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
  margin: 0.75rem 1rem 0; padding: 0.75rem 1rem;
  background: var(--brand-primary-soft); border: 1px solid #dbe5f1; border-radius: 12px;
}
.pwa-ico { color: var(--brand-primary); display: flex; }
.pwa-text { flex: 1 1 220px; display: flex; flex-direction: column; gap: 0.15rem; font-size: 0.85rem; color: #475569; }
.pwa-text strong { font-size: 0.9rem; color: #0f172a; font-weight: 600; }
.pwa-btn {
  padding: 0.5rem 1rem; border: none; border-radius: 10px; cursor: pointer;
  background: var(--brand-primary); color: #fff; font-size: 0.85rem; font-weight: 500;
}
.pwa-btn:disabled { opacity: 0.6; cursor: default; }
.pwa-x { background: transparent; border: none; color: #64748b; font-size: 0.85rem; cursor: pointer; padding: 0.5rem; }
.pwa-x:hover { color: #0f172a; }
</style>
